import { Button, FormControl, TextField } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';
import { useUpdateDesignFolder } from '~/api/design-folders';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import type { RenameFolderDialogProps__DesignFolder } from './StudioRenameFolderDialog.generated';

/* GraphQL */ `#graphql
fragment RenameFolderDialogProps__DesignFolder on DesignFolder {
  id
  name
}
`;

interface RenameFolderDialogProps {
  designFolder: RenameFolderDialogProps__DesignFolder;
  onClose: () => void;
  open: boolean;
}

export const StudioRenameFolderDialog = ({
  designFolder,
  onClose,
  open,
}: RenameFolderDialogProps) => {
  const { t } = useTranslation();
  const [update] = useUpdateDesignFolder();

  const formik = useFormik({
    initialValues: { name: designFolder.name },
    onSubmit: async (values) => {
      const { errors } = await update({
        variables: {
          input: {
            designFolderId: designFolder.id,
            patch: { name: values.name },
          },
        },
      });
      if (!errors?.length) onClose();
    },
    enableReinitialize: true,
    validateOnBlur: true,
    validationSchema: object({
      name: string().required().trim(),
    }),
  });

  const cancel = () => {
    onClose();
    formik.resetForm({ values: { name: designFolder.name } });
  };

  return (
    <Dialog open={open} onClose={cancel} maxWidth="sm" fullWidth>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle onClose={cancel}>{t('pages.studio.dialogs.rename_folder.title')}</DialogTitle>

        <DialogContent>
          <FormControl fullWidth>
            <TextField
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              label={t('common.labels.name')}
              margin="dense"
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
              variant="outlined"
              focused
              autoFocus
            />
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={cancel}>
            {t('common.dialogs.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          >
            {t('common.dialogs.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
